import { useEffect, useState } from "react";

import {
  buildFileUpdateSizeLimitText,
  buildFileUploadPrompt,
} from "@smart/bridge-intake-components-dom";
import { defaultAIFillSettings } from "@smart/bridge-types-basic";
import {
  Alert,
  Card,
  EditableText,
  FileUpload,
} from "@smart/itops-sb-design-system-dom";

import { baseLanguageKey, LanguageKey } from "../../../../types";
import { GqlForm, FormTranslation, UpdateAIFillSettings } from "../../types";

const defaultMaxNumOfFiles = 5;

type AIFillFileUploadProps = {
  aiFillSettings: GqlForm["aiFillSettings"];
  updateAIFillSettings: UpdateAIFillSettings;
  viewingLanguage: LanguageKey;
  formTranslation?: FormTranslation | null;
  disabled?: boolean;
};

export const AIFillFileUpload = ({
  aiFillSettings,
  updateAIFillSettings,
  viewingLanguage,
  formTranslation,
  disabled,
}: AIFillFileUploadProps) => {
  const isViewingBaseLanguage = viewingLanguage === baseLanguageKey;

  const baseDescription =
    aiFillSettings?.fileUploadDescription ||
    defaultAIFillSettings.fileUploadDescription;
  const translatedDescription = formTranslation?.aiFillFileUploadDescription;
  const displayedDescription = isViewingBaseLanguage
    ? baseDescription
    : translatedDescription || baseDescription;

  const [editingDescription, setEditingDescription] =
    useState(displayedDescription);

  useEffect(() => {
    setEditingDescription(displayedDescription);
  }, [displayedDescription]);

  const forLanguage = isViewingBaseLanguage ? undefined : viewingLanguage;

  const sizeLimit = buildFileUpdateSizeLimitText(
    {
      maxFiles: defaultMaxNumOfFiles,
      fileTypeNames: ["jpeg", "pdf", "png", "doc", "docx"],
      sizeLimit: 5,
    },
    viewingLanguage,
  );
  const uploadPrompt = buildFileUploadPrompt(true, viewingLanguage);

  return (
    <div className="max-w-screen-readable m-auto flex flex-col gap-medium">
      <Alert
        content="Please revise text below to give clients enough information to choose
          the correct documents to upload."
        variant="section"
        palette="warning"
      />
      <Card size="readable" disabled={disabled}>
        <div className="mb-[1rem]">
          <EditableText
            dataTestId="ai-fill-file-upload-description"
            text={editingDescription}
            onChange={setEditingDescription}
            onComplete={() =>
              updateAIFillSettings(
                {
                  fileUploadDescription: editingDescription,
                },
                forLanguage,
              )
            }
            fontSize="paragraph"
            isEditor
            disabled={disabled}
          />
        </div>
        <FileUpload
          id=""
          errorId=""
          sizeLimit={sizeLimit}
          uploadPrompt={uploadPrompt}
          onSelect={() => {}}
          disabled={disabled}
        />
      </Card>
    </div>
  );
};
